/* =============================================================
   Anonymous-mode storage for the Psych Safety Assessment.

   Mirrors the shape of the Supabase rows so the two storage
   layers are interchangeable. When a user is signed in we read
   and write via the queries.ts hooks (Supabase). When they're
   anonymous we read and write via the helpers below (localStorage).

   On sign-in we migrate any local draft / result into the DB,
   then clear the local copies.
   ============================================================ */

import type { Responses } from './psychSafety';

const KEY_DRAFT  = 'ps:draft:v2';
const KEY_RESULT = 'ps:result:v2';

export interface LocalDraft {
  responses: Responses;
  stage_idx: number;
  updated_at: string;
}

export interface LocalResult {
  responses: Responses;
  completed_at: string;
}

function safeGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private-mode Safari etc — silently ignore */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------
export function getLocalDraft(): LocalDraft | null {
  return safeGet<LocalDraft>(KEY_DRAFT);
}

export function setLocalDraft(d: { responses: Responses; stage_idx: number }): void {
  const payload: LocalDraft = {
    responses: d.responses,
    stage_idx: d.stage_idx,
    updated_at: new Date().toISOString(),
  };
  safeSet(KEY_DRAFT, payload);
}

export function clearLocalDraft(): void {
  safeRemove(KEY_DRAFT);
}

// ---------------------------------------------------------------
// Results
// ---------------------------------------------------------------
export function getLocalResult(): LocalResult | null {
  return safeGet<LocalResult>(KEY_RESULT);
}

export function setLocalResult(r: Responses): void {
  safeSet(KEY_RESULT, { responses: r, completed_at: new Date().toISOString() } satisfies LocalResult);
  clearLocalDraft(); // mirror the server-side behaviour
}

export function clearLocalResult(): void {
  safeRemove(KEY_RESULT);
}

// ---------------------------------------------------------------
// Sign-in migration — promote local state into the DB
// ---------------------------------------------------------------

import { supabase } from './supabase';
import { allStageAverages, overallAverage, overallZoneCounts } from './psychSafety';

/**
 * If the user just signed in and has a local result or draft, save them
 * to Supabase under their new account, then clear local copies.
 * Idempotent: safe to call any time after sign-in.
 */
export async function migrateLocalToServer(userId: string): Promise<{
  result_migrated: boolean;
  draft_migrated: boolean;
}> {
  let result_migrated = false;
  let draft_migrated = false;

  const localResult = getLocalResult();
  if (localResult && Object.keys(localResult.responses).length > 0) {
    const overall = overallAverage(localResult.responses);
    const stage_scores = allStageAverages(localResult.responses);
    const zc = overallZoneCounts(localResult.responses);
    const { error } = await supabase.from('assessment_responses').insert({
      user_id: userId,
      responses: localResult.responses,
      overall_score: Number(overall.toFixed(2)),
      stage_scores,
      zone_counts: { red: zc.red, neutral: zc.neutral, blue: zc.blue, total: zc.total },
      completed_at: localResult.completed_at,
    });
    if (!error) {
      result_migrated = true;
      clearLocalResult();
    }
  } else {
    // Only migrate draft if there's no result to migrate (a finished result
    // supersedes any half-finished draft).
    const localDraft = getLocalDraft();
    if (localDraft && Object.keys(localDraft.responses).length > 0) {
      const { error } = await supabase.from('assessment_drafts').upsert({
        user_id: userId,
        responses: localDraft.responses,
        stage_idx: localDraft.stage_idx,
      });
      if (!error) {
        draft_migrated = true;
        clearLocalDraft();
      }
    }
  }

  return { result_migrated, draft_migrated };
}
